// Recibo unificado: soma vários pagamentos num só recibo (automático por ano letivo ou manual por período/categoria)
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Receipt, Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const B = '/financial/recibo-unificado';

export default function ReciboUnificadoDialog({ open, onOpenChange, studentId, studentName, year }) {
  const [mode, setMode] = useState('year');
  const [y, setY] = useState(year || new Date().getFullYear());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cats, setCats] = useState([]);
  const [siblings, setSiblings] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = useCallback(() => {
    const p = { student_id: studentId, mode, include_siblings: siblings };
    if (mode === 'year' && y) p.year = y;
    if (mode === 'period') {
      if (from) p.date_from = from;
      if (to) p.date_to = to;
    }
    if (cats.length) p.categories = cats.join(',');
    return p;
  }, [studentId, mode, y, from, to, cats, siblings]);

  const erro = (e, fb) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : fb);
  };

  const load = useCallback(async () => {
    if (!open || !studentId) return;
    setBusy(true);
    try {
      const { data: d } = await api.get(`${B}/preview`, { params: params() });
      setData(d);
      if (mode === 'year' && d.years?.length && !d.years.includes(Number(y))) setY(d.years[0]);
    } catch (e) {
      erro(e, 'Não foi possível carregar os pagamentos.');
    } finally { setBusy(false); }
  }, [open, studentId, params, mode, y]);

  useEffect(() => { load(); }, [load]);

  const abrir = async () => {
    setBusy(true);
    try {
      const r = await api.get(`${B}/recibo.jpg`, { params: params(), responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) {
      erro(e, 'Não foi possível gerar o recibo.');
    } finally { setBusy(false); }
  };

  const enviar = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`${B}/share`, { params: params() });
      const url = `${process.env.REACT_APP_BACKEND_URL}${d.image_path}`;
      const msg = encodeURIComponent(`${d.text}${url}`);
      window.open(d.phone ? `https://wa.me/${d.phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
      toast.success(d.phone ? `Recibo pronto para ${d.payer_name || 'o responsável'}.`
        : 'Responsável sem telefone: escolha o contato no WhatsApp.');
    } catch (e) {
      erro(e, 'Não foi possível preparar o recibo.');
    } finally { setBusy(false); }
  };

  const toggleCat = (c) => setCats((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="recibo-unificado-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-violet-600" /> Recibo unificado
          </DialogTitle>
          <DialogDescription>
            Um recibo só, somando os pagamentos de {studentName || data?.student_name || 'aluno'}.
            Escolha automático por ano letivo ou filtre por período e categoria.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5">
          {[['year', 'Automático por ano letivo'], ['period', 'Manual por período']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`ru-mode-${k}`}
              className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${mode === k ? 'bg-violet-700 text-white border-violet-700' : 'bg-white hover:bg-slate-50'}`}>
              {l}
            </button>
          ))}
        </div>

        {mode === 'year' ? (
          <div className="flex flex-wrap gap-1.5" data-testid="ru-years">
            {(data?.years?.length ? data.years : [y]).map((opt) => (
              <button key={opt} type="button" onClick={() => setY(opt)} data-testid={`ru-year-${opt}`}
                className={`text-xs font-semibold rounded-lg border px-3 py-1.5 ${Number(y) === Number(opt) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
                {opt}
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">De</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ru-from" />
            </div>
            <div>
              <label className="text-xs font-semibold">Até</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ru-to" />
            </div>
          </div>
        )}

        {!!data?.categories?.length && (
          <div>
            <p className="text-xs font-semibold mb-1.5">Categorias (nenhuma marcada = todas)</p>
            <div className="flex flex-wrap gap-1.5" data-testid="ru-categories">
              {data.categories.map((c) => (
                <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`ru-cat-${c}`}
                  className={`text-[11px] font-medium rounded-full border px-2.5 py-1 capitalize ${cats.includes(c) ? 'bg-violet-100 border-violet-400 text-violet-800' : 'bg-white hover:bg-slate-50'}`}>
                  {String(c).replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={siblings} onCheckedChange={(v) => setSiblings(!!v)} data-testid="ru-siblings" />
          <Users className="w-4 h-4 text-violet-600" /> Incluir irmãos (recibo da família)
        </label>

        <div className="rounded-xl border bg-slate-50 p-3" data-testid="ru-preview">
          {busy && !data ? (
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando pagamentos…
            </p>
          ) : !data?.count ? (
            <p className="text-sm text-slate-500">Nenhum pagamento encontrado para os filtros escolhidos.</p>
          ) : (
            <>
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-semibold uppercase text-slate-500">{data.period_label}</span>
                <b className="text-lg text-violet-700" data-testid="ru-total">{brl(data.total)}</b>
              </div>
              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto">
                {data.groups.map((g) => (
                  <div key={g.name}>
                    <p className="text-[12px] font-bold text-violet-800">{g.name} · {brl(g.subtotal)}</p>
                    {g.items.map((i) => (
                      <div key={i.title_id} className="flex justify-between text-[12px] text-slate-700">
                        <span className="truncate pr-2">{i.date} · {i.description}{i.partial ? ' (parcial)' : ''}</span>
                        <span className="font-medium">{brl(i.amount)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 sm:flex-none">Fechar</Button>
          <Button variant="outline" onClick={abrir} disabled={busy || !data?.count} data-testid="ru-open"
            className="flex-1 sm:flex-none">
            <Receipt className="w-4 h-4 mr-1.5" /> Abrir recibo (JPEG)
          </Button>
          <Button onClick={enviar} disabled={busy || !data?.count} data-testid="ru-send"
            className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700">
            <Send className="w-4 h-4 mr-1.5" /> Enviar no WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
